/**
 * Constellation overlays, ported from loader-overlay.js: for each key in the
 * store's overlays list the TLE group is fetched through the CelesTrak proxy
 * (fetchTleCatalog), the name filter applied (Sentinel) and the satellites
 * handed to engine.addOverlay with the constellation colour; removeOverlay on
 * toggle off. Overlays are drawn, not analysed. Id prefixes match the original.
 */
import { OVERLAYS } from '../constants';
import { loaderApi } from '../data/api';
import { fetchTleCatalog, type TleSatellite } from '../engine';
import type { OverlaySatellite } from '../globe/GlobeEngine';
import { useAwareness, type OverlayState } from './store';

/** Satellite id prefixes per overlay as loader-overlay.js assigned them. */
export const OVERLAY_ID_PREFIX: Record<string, string> = {
  starlink: 'starlink_',
  oneweb: 'oneweb_',
  stations: 'station_',
  gnss_gps: 'gnss_gps_',
  gnss_glonass: 'gnss_glo_',
  gnss_galileo: 'gnss_gal_',
  gnss_beidou: 'gnss_bei_',
  sentinel: 'sentinel_',
  satnogs: 'satnogs_',
};

export type OverlayDef = (typeof OVERLAYS)[number];

export function overlayDef(key: string): OverlayDef | undefined {
  return OVERLAYS.find((o) => o.key === key);
}

export function overlayIdPrefix(key: string): string {
  return OVERLAY_ID_PREFIX[key] ?? key + '_';
}

export function filterOverlaySats<T extends { name: string }>(sats: T[], filter?: RegExp): T[] {
  return filter ? sats.filter((s) => filter.test(s.name)) : sats;
}

export function toOverlaySatellite(s: TleSatellite): OverlaySatellite {
  return { id: s.id, name: s.name, tle1: s.tle1, tle2: s.tle2 };
}

export type TleFetcher = (url: string, options: { idPrefix: string; signal?: AbortSignal }) => Promise<{ satellites: TleSatellite[] }>;

/** Fetch and filter one overlay's catalogue and store it; throws when the key is unknown or the fetch fails. */
export async function loadOverlay(
  key: string,
  options: { signal?: AbortSignal; fetchCatalog?: TleFetcher } = {},
): Promise<OverlayState> {
  const def = overlayDef(key);
  if (!def) throw new Error(`Unknown overlay: ${key}`);
  const fetcher = options.fetchCatalog ?? fetchTleCatalog;
  const url = loaderApi.celestrakUrl(def.group, { supplemental: def.supplemental });
  const result = await fetcher(url, { idPrefix: overlayIdPrefix(key), signal: options.signal });
  const sats = filterOverlaySats(result.satellites, def.filter).map(toOverlaySatellite);
  const state: OverlayState = { label: def.label, color: def.color, sats };
  useAwareness.getState().setOverlay(key, state);
  return state;
}
